import { useState } from 'react';
import { Box, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assignment, Business, Description, Folder, Home, Language, ListAlt,
  MoreHoriz, Place, Schedule as ScheduleIcon, Science,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useGuardedNavigate } from '../../hooks/useUnsavedGuard';

export const MOBILE_BOTTOM_NAV_HEIGHT = 58;

interface NavTab {
  label: string;
  icon: React.ReactNode;
  path?: string;
  /** Path prefixes that light this tab up as active. */
  activePrefixes: string[];
}

const TABS: NavTab[] = [
  { label: 'Home', icon: <Home sx={{ fontSize: 21 }} />, path: ROUTES.DASHBOARD, activePrefixes: [ROUTES.DASHBOARD] },
  { label: 'My Tests', icon: <Science sx={{ fontSize: 21 }} />, path: ROUTES.MY_TESTS, activePrefixes: [ROUTES.MY_TESTS] },
  { label: 'Work Orders', icon: <Assignment sx={{ fontSize: 21 }} />, path: ROUTES.WORK_ORDERS_TAB, activePrefixes: [ROUTES.WORK_ORDERS_TAB, '/projects'] },
  { label: 'More', icon: <MoreHoriz sx={{ fontSize: 21 }} />, activePrefixes: [] },
];

const MORE_ITEMS = [
  { label: 'Projects', icon: <Folder sx={{ fontSize: 18 }} />, path: ROUTES.PROJECTS },
  { label: 'Tests (full grid)', icon: <ListAlt sx={{ fontSize: 18 }} />, path: ROUTES.TESTS },
  { label: 'Schedule', icon: <ScheduleIcon sx={{ fontSize: 18 }} />, path: ROUTES.SCHEDULE },
  { label: 'Quotes', icon: <Description sx={{ fontSize: 18 }} />, path: ROUTES.QUOTES },
  { label: 'Clients', icon: <Business sx={{ fontSize: 18 }} />, path: ROUTES.CLIENTS },
  { label: 'Sites', icon: <Language sx={{ fontSize: 18 }} />, path: ROUTES.SITES },
  { label: 'Locations', icon: <Place sx={{ fontSize: 18 }} />, path: ROUTES.LOCATIONS_MANAGE },
  { label: 'Samples', icon: <Science sx={{ fontSize: 18 }} />, path: ROUTES.SAMPLES },
];

/**
 * Phone/tablet bottom navigation (mockups 2026-07-24, Option N1): the fast
 * path is Home → My Tests → a test entry page; everything else lives under
 * "More". Rendered by AppShell below the md breakpoint only — the desktop top
 * nav is untouched.
 */
export const MobileBottomNav = () => {
  const guardedNavigate = useGuardedNavigate();
  const location = useLocation();
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  return (
    <Box sx={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: (t) => t.zIndex.drawer + 1,
      height: MOBILE_BOTTOM_NAV_HEIGHT,
      display: 'flex', alignItems: 'stretch',
      backgroundColor: '#3E4A3E',
      borderTop: `1px solid ${alpha('#8CA882', 0.2)}`,
      boxShadow: `0 -2px 10px ${alpha('#1A231A', 0.3)}`,
      pb: 'env(safe-area-inset-bottom)',
    }}>
      {TABS.map((tab) => {
        const isActive = tab.activePrefixes.some((p) => location.pathname.startsWith(p));
        return (
          <Box
            key={tab.label}
            onClick={(e) => (tab.path ? guardedNavigate(tab.path) : setMoreAnchor(e.currentTarget))}
            sx={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 0.25, cursor: 'pointer',
              color: isActive ? '#FFFFFF' : alpha('#FFFFFF', 0.55),
              transition: 'color 0.15s ease',
              '&:active': { backgroundColor: alpha('#FFFFFF', 0.08) },
            }}
          >
            {tab.icon}
            <Typography sx={{ fontSize: '0.6rem', fontWeight: isActive ? 700 : 500, lineHeight: 1 }}>
              {tab.label}
            </Typography>
          </Box>
        );
      })}

      <Menu
        anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200, mb: 1, borderRadius: 2 } } }}
      >
        {MORE_ITEMS.flatMap((item, i) => [
          // Divider splits daily-work pages from the directory registries
          ...(i === 4 ? [<Divider key={`div-${item.label}`} sx={{ my: 0.5 }} />] : []),
          <MenuItem
            key={item.label}
            selected={location.pathname.startsWith(item.path)}
            onClick={() => { setMoreAnchor(null); guardedNavigate(item.path); }}
            sx={{ fontSize: '0.85rem', gap: 1.25, py: 1 }}
          >
            {item.icon}{item.label}
          </MenuItem>,
        ])}
      </Menu>
    </Box>
  );
};
